<template>
    <b-modal
        size="md"
        :visible="show"
        @hidden="hideEvents"
        :id="modalId"
        :title="title"
        class="note-modal"
        scrollable
    >
        <vue-element-loading :active="loading"></vue-element-loading>
        <p v-if="this.clientActionIds.length > 1">
            Are you sure you want to add a note to <b>{{ this.clientActionIds.length }}</b> Actions?
        </p>
        <b-form>
            <slot></slot>
            <b-form-textarea
                    class="mb-3"
                    :placeholder="placeholder"
                    rows="6"
                    v-model="$v.form.note.$model"
                    :state="getState($v.form.note)"
            >
            </b-form-textarea>

            <form-error v-if="$v.form.$error && !fileUrl">
                {{ error }}
            </form-error>

            <vue-dropzone
                    v-if="fileUrl"
                    id="fileDrop"
                    ref="dropzone"
                    :options="dropOptions"
                    @vdropzone-sending-multiple="attachParams"
                    @vdropzone-success-multiple="onFileSuccess"
                    @vdropzone-queue-complete="onQueueComplete"
                    @vdropzone-error="onFileError"
                    @vdropzone-file-added="checkForDuplicates"
            >
            </vue-dropzone>
        </b-form>

        <confirmation-warning
                v-if="showWarningMessage"
                :numberOfItems="this.nodeIds.length"
                @unlocked="unlocked = true">
        </confirmation-warning>

        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    class="cancel"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    class="btn-arrow"
                    variant="primary"
                    @click="onSendNote"
                    :disabled="disabled"
            >
                {{ buttonText }}
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import ModalMixin from '@/components/Modal/ModalMixin';
import FormValidate from '@/services/Mixins/FormValidate';
import { required } from 'vuelidate/lib/validators';
import FileDropMixin from '@/components/Form/FileDropMixin';
import { singleRequestUploadOptions } from '@/components/Form/singleRequestUpload';
import ConfirmationWarning from '@/components/Notifications/ConfirmationWarning'

export default {
  name: 'Note-Modal',
  components: {
    ConfirmationWarning,
  },
  props: {
    show: {
      default: false,
    },
    title: {
      default: 'Add a note',
    },
    busy: {
      default: false,
    },
    sentNoteFn: {
      type: Function,
    },
    buttonText: {
      default: 'Send note',
    },
    notePlaceholder: {
      type: String,
    },
    errorMessage: {
      type: String,
    },
    showWarningMessage: {
      default: false,
    },
    clientActionIds: {
      default: Array,
    },
  },
  mixins: [FileDropMixin, ModalMixin, FormValidate],
  data() {
    return {
      modalId: 'noteModal',
      form: {
        note: '',
      },
      unlocked: true,
    };
  },
  created() {
    this.dropOptions.paramName = 'files';
    Object.assign(this.dropOptions, singleRequestUploadOptions);
  },
  computed: {
    placeholder() {
      return this.notePlaceholder || 'Write a note...';
    },
    error() {
      return this.errorMessage || 'Please enter a message.';
    },
    disabled() {
      return (this.$v.form.$invalid || this.loading) && this.unlocked;
    },
  },
  watch: {
    busy: {
      handler(newBusy) {
        this.loading = newBusy;
      },
      immediate: true,
    },
  },
  validations: {
    form: {
      note: {
        required(value) {
          if (this.fileUrl) {
            return true;
          }
          return required(value);
        },
      },
    },
  },
  methods: {
    async onSendNote() {
      if (this.loading) {
        return false;
      }

      this.loading = true;

      // Note is required
      if (this.form.note === '') {
        this.loading = false;
        this.showErrorRequired();

        return false;
      }

      // File route, post via dropzone
      if (this.fileUrl && this.hasFiles()) {
        // We don't need to set loading to false on this path
        // Dropzone calls a separate method when all the uploads are done
        // That is where we set loading to false
        this.processFiles();

        return true;
      }

      try {
        await this.sentNoteFn(this.form);
        this.$emit('sent', this.form);
      } finally {
        this.loading = false;
      }

      return true;
    },

    hideModalEvents() {
      this.form.note = '';
      this.$emit('update:show');
    },

    // Fires once per request, and only when it succeeded. Dropzone also
    // completes the queue for refused files and failed requests.
    onFileSuccess() {
      this.$emit('file-sent');
    },

    attachParams(files, xhr, formData) {
      formData.append('note', this.form.note);

      if (Array.isArray(this.clientActionIds)) {
        this.clientActionIds.forEach((id) => {
          formData.append('clientActionIds[]', id);
        });
      }
    },

    showErrorRequired() {
      const title = 'Note is required';
      const text = '';
      const type = 'error';

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });
    },
  },
};
</script>

<style lang="scss">
</style>
